import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useVerseSubmissions } from '../hooks/useVerseSubmissions';
import { supabase } from '../services/supabase';
import { Verse, VerseStatus } from '../types/content';
import './VerseSubmissions.css';

const STATUS_TABS: { value: VerseStatus; label: string }[] = [
  { value: 'pending', label: 'Waiting on you' },
  { value: 'approved', label: 'Published' },
  { value: 'declined', label: 'Passed on' },
];

function resolveAudioUrl(fileUrl: string): string {
  if (!fileUrl) return '';
  if (fileUrl.startsWith('http')) return fileUrl;
  const { data } = supabase.storage.from('verses').getPublicUrl(fileUrl);
  return data?.publicUrl || '';
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' });
}

interface RowProps {
  verse: Verse;
  beatTitle: string;
  busy: boolean;
  status: VerseStatus;
  onApprove: () => void;
  onDecline: () => void;
}

const SubmissionRow: React.FC<RowProps> = ({
  verse,
  beatTitle,
  busy,
  status,
  onApprove,
  onDecline,
}) => {
  const { play, pause, isPlaying, currentAudioKey, currentTime, duration, formatTime, stop } =
    useAudioPlayer();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioError, setAudioError] = useState(false);
  const audioUrl = resolveAudioUrl(verse.file_url);
  const isThisPlaying = isPlaying && currentAudioKey === verse.id;

  const togglePlay = () => {
    if (!audioRef.current || !audioUrl) {
      setAudioError(true);
      return;
    }
    if (isThisPlaying) {
      pause();
      return;
    }
    play(audioRef.current, verse.id).catch(() => setAudioError(true));
  };

  const redoMessage = `Hey! About your verse on "${beatTitle}" — could you record another take?`;

  return (
    <li className="subs-page__row">
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => stop()}
          onError={() => setAudioError(true)}
          preload="none"
          className="hidden"
        />
      )}

      <button
        type="button"
        className="subs-page__play"
        onClick={togglePlay}
        aria-label={isThisPlaying ? 'Pause verse' : 'Play verse'}
        disabled={audioError}
      >
        {isThisPlaying ? (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M8 5.14v13.72L19 12z" />
          </svg>
        )}
      </button>

      <div className="subs-page__row-body">
        <div className="subs-page__row-top">
          <Link to={`/profile/${verse.rapper_id}`} className="subs-page__rapper">
            {verse.rapper?.username || verse.rapper_username}
          </Link>
          <span className="subs-page__row-time">
            {isThisPlaying
              ? `${formatTime(currentTime)} / ${formatTime(duration)}`
              : timeAgo(verse.created_at)}
          </span>
        </div>
        <p className="subs-page__row-hint">
          {audioError
            ? 'This take could not be loaded.'
            : 'Full mix — the beat with their vocal on top.'}
        </p>
      </div>

      <div className="subs-page__row-actions">
        {status !== 'approved' && (
          <button
            type="button"
            className="subs-page__btn subs-page__btn--approve"
            onClick={onApprove}
            disabled={busy}
          >
            {busy ? 'Saving…' : status === 'declined' ? 'Publish after all' : 'Approve & publish'}
          </button>
        )}
        <Link
          to={`/messages?to=${verse.rapper_id}&draft=${encodeURIComponent(redoMessage)}`}
          className="subs-page__btn subs-page__btn--redo"
        >
          Ask for a redo
        </Link>
        {status !== 'declined' && (
          <button
            type="button"
            className="subs-page__btn subs-page__btn--decline"
            onClick={onDecline}
            disabled={busy}
          >
            Pass
          </button>
        )}
      </div>
    </li>
  );
};

const VerseSubmissions: React.FC = () => {
  const { authState } = useAuth();
  const me = authState.user;
  const isProducer = me?.role === 'producer' || me?.role === 'producer_rapper';

  const { groups, loading, error, review, busyVerseId, statusFilter, setStatusFilter } =
    useVerseSubmissions(isProducer ? me?.id : undefined);

  const [heroReady, setHeroReady] = useState(false);
  useEffect(() => {
    const t = requestAnimationFrame(() => setHeroReady(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const totalShown = groups.reduce((sum, group) => sum + group.verses.length, 0);

  const emptyCopy: Record<VerseStatus, string> = {
    pending: 'No verses waiting on you. When a rapper drops a take on one of your collaboration beats, it lands here first.',
    approved: 'Nothing published from a collaboration beat yet.',
    declined: 'You haven\'t passed on any takes.',
  };

  return (
    <div className="subs-page">
      <Helmet>
        <title>DHH Collab | Verse submissions</title>
      </Helmet>

      <div className="subs-page__bg" aria-hidden>
        <span className="subs-page__blob subs-page__blob--1" />
        <span className="subs-page__blob subs-page__blob--2" />
        <span className="subs-page__grid-pattern" />
      </div>

      <section className={`subs-page__hero ${heroReady ? 'subs-page__hero--visible' : ''}`}>
        <div className="subs-page__wrap container-padding">
          <span className="subs-page__eyebrow">Collaboration</span>
          <h1 className="subs-page__title">Verse submissions</h1>
          <p className="subs-page__subtitle">
            Takes recorded on your collaboration beats land here first. Listen, then publish
            the ones you want the world to hear.
          </p>
        </div>
      </section>

      <div className="subs-page__body subs-page__wrap container-padding">
        {!isProducer ? (
          <div className="subs-page__gate">
            <p className="subs-page__gate-text">
              This queue is for producers reviewing verses recorded on their beats.
            </p>
            <Link to="/home" className="subs-page__gate-cta">
              Back to home
            </Link>
          </div>
        ) : (
          <>
            <div className="subs-page__tabs" role="tablist" aria-label="Submission status">
              {STATUS_TABS.map(tab => (
                <button
                  key={tab.value}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === tab.value}
                  className={`subs-page__tab ${
                    statusFilter === tab.value ? 'subs-page__tab--active' : ''
                  }`}
                  onClick={() => setStatusFilter(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {error && (
              <p className="subs-page__error" role="alert">
                {error}
              </p>
            )}

            {loading ? (
              <p className="subs-page__status">Loading submissions…</p>
            ) : totalShown === 0 ? (
              <div className="subs-page__empty">
                <span className="subs-page__empty-icon" aria-hidden>
                  🎧
                </span>
                <p className="subs-page__empty-text">{emptyCopy[statusFilter]}</p>
                <Link to="/upload" className="subs-page__gate-cta">
                  Upload a collaboration beat
                </Link>
              </div>
            ) : (
              <div className="subs-page__groups">
                {groups.map(group => (
                  <section className="subs-page__group" key={group.beatId}>
                    <header className="subs-page__group-head">
                      <div>
                        <Link to={`/beats/${group.beatId}`} className="subs-page__beat-title">
                          {group.beatTitle}
                        </Link>
                        <p className="subs-page__beat-meta">
                          {group.genre} · {group.bpm} BPM
                        </p>
                      </div>
                      <span className="subs-page__group-count">
                        {group.verses.length} {group.verses.length === 1 ? 'take' : 'takes'}
                      </span>
                    </header>

                    <ul className="subs-page__rows">
                      {group.verses.map(verse => (
                        <SubmissionRow
                          key={verse.id}
                          verse={verse}
                          beatTitle={group.beatTitle}
                          busy={busyVerseId === verse.id}
                          status={statusFilter}
                          onApprove={() => review(verse.id, 'approved')}
                          onDecline={() => review(verse.id, 'declined')}
                        />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerseSubmissions;
